/**
 * Created by r4zi4l on 17.06.2022
 */

/**
 * Registry of reward type descriptors, filled by Reward.register.
 * Every descriptor inherits RewardType.prototype, so a library may change a default for all types
 * (see aisensia/src/utils/rewards/rewardtypes.js) regardless of the order the types were registered in.
 */
const RewardTypes = {};

/**
 * Descriptor hooks, all optional. `value` is the value stored in the Reward, `options` the Reward options.
 *   parse(value, options)              config value -> Reward[]; default creates one Reward
 *   parseValue(value)                  normalizes the config value before it is stored in the Reward
 *   getAmount(value)                   numeric amount: visibility, delta text, flying icons count
 *   getText(value, prefix)             label in reward lists
 *   getTarget(value, options, source)  aim target name(s) the reward flies to; default `target` or the type name
 *   getFlyingAmount(amount, value)     number of icons in the collect animation
 *   getFlyEffect(value)                sound of the collect animation when the caller passes none
 *   getPrice(value)                    price in hard currency for RewardsList.calcCost
 *   matches(value, key)                whether the Reward corresponds to the config entry { [type]: { [key]: amount } }
 *   sumValues(value, other)            merges two config values of this type for RewardsList.sumRewards (other may be undefined)
 *   isBigReward(value)                 a reward worth sharing (RewardWindow)
 *   getIcon(value, options)            sprite frame or a ready cc.Node; getSmallIcon(value) frame; getAnimation(value) spine params
 *   receive(value, options)            gives the reward; called with the Reward as `this`; may return a callback that refreshes the view
 *                                      (stored as reward.viewUpdate and run when the collect animation finishes)
 *   check(rewards, options)            validates before receiving; returns { message } on error
 *   controls, bundle, flyingAnimation, invisible  static properties
 *   countable                          counted in pieces: labeled "x3" instead of a bare amount
 *   currency                           a wallet currency (hard, soft, ...); everything else is an item for RewardsList.calcCostByKind
 *
 * Example:
 *   Reward.register("stars", {
 *       flyingAnimation: Reward.COLLECT_ANIMATION,
 *
 *       getIcon: function () {
 *           return bundles.reward_icons.frames.stars_png;
 *       },
 *
 *       receive: function (value) {
 *           cleverapps.starChest.addStars(value, true); // model changes at once, silently
 *
 *           return function () { // reward.viewUpdate: runs when the icon lands on its target
 *               cleverapps.starChest.onChangeStars();
 *           };
 *       }
 *   });
 */
const RewardType = function () {};

RewardType.prototype.parseValue = function (value) {
    return value;
};

RewardType.prototype.getAmount = function (value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "object") {
        return value.amount || 0;
    }
    return value || 0;
};

RewardType.prototype.getPrefix = function (prefix) {
    if (!prefix || (prefix === RewardType.DEFAULT_PREFIX && !this.countable)) {
        return "";
    }
    return prefix;
};

RewardType.prototype.getText = function (value, prefix) {
    return this.getPrefix(prefix) + (Array.isArray(value) ? value.join("-") : this.getAmount(value));
};

RewardType.prototype.getTarget = function () {
    return this.target || this.type;
};

RewardType.prototype.getFlyingAmount = function (amount) {
    if (amount >= 100) {
        return 8;
    }
    if (amount >= 50) {
        return 5;
    }
    if (amount >= 3) {
        return 3;
    }
    return amount;
};

RewardType.prototype.getFlyEffect = function () {
    return undefined;
};

RewardType.prototype.getStartReceiveRewardEffect = function () {
    return undefined;
};

RewardType.prototype.getCollectRewardEffect = function () {
    return undefined;
};

RewardType.prototype.getPrice = function () {
    return 0;
};

RewardType.prototype.matches = function (value, key) {
    return value.id === key;
};

RewardType.prototype.sumValues = function (value, other) {
    if (typeof value === "number") {
        return value + (other || 0);
    }
    if (typeof value !== "object" || value === null) {
        return value;
    }

    const result = { ...other };
    Object.keys(value).forEach((key) => {
        result[key] = RewardType.prototype.sumValues(value[key], other && other[key]);
    });
    return result;
};

RewardType.prototype.isBigReward = function () {
    return false;
};

RewardType.DEFAULT_PREFIX = "x";

const Reward = function (type, value, options) {
    this.type = type;
    this.descriptor = RewardTypes[type];

    if (!this.descriptor) {
        throw new Error(`Unknown reward type: ${type}`);
    }

    this.value = this.descriptor.parseValue(value);
    this.options = options || {};
};

/**
 * Registers a reward type. `types` may be an array of aliases sharing one descriptor; the first one is the descriptor type.
 * Re-registering a type replaces the previous descriptor; the last loaded file wins, which is reported in debug mode.
 */
Reward.register = function (types, descriptor) {
    Object.setPrototypeOf(descriptor, RewardType.prototype);

    cleverapps.toArray(types).forEach((type, index) => {
        if (index === 0) {
            descriptor.type = type;
        }
        if (RewardTypes[type] && cleverapps.config.debugMode) {
            console.warn(`Reward type "${type}" registered twice, the later registration wins`);
        }
        RewardTypes[type] = descriptor;
    });

    return descriptor;
};

/**
 * Hooks for rewards whose value is a time period ("2 hours", ms, or a subscription product): unlimitedLives, worker, energyBooster.
 * Spread into the descriptor: Reward.register("worker", { ...Reward.TimeValue, ... })
 */
Reward.TimeValue = {
    parseValue: function (value) {
        return Product.FormatTimePeriod(value);
    },

    getAmount: function (value) {
        return value.amount || value.time || +value.subscription || 0;
    },

    getFlyingAmount: function () {
        return 1;
    },

    getText: function (value, prefix) {
        const title = Messages.get(value.title, value.toReplace);
        return value.subscription ? this.getPrefix(prefix) + (value.amount || 1) : title;
    },

    getPrice: function (value) {
        const hours = value.time / 1000 / 3600;
        return hours * Reward.TIME_VALUE_PRICE_PER_HOUR;
    },

    sumValues: function (value, other) {
        const time = cleverapps.parseInterval(value) + (other ? cleverapps.parseInterval(other) : 0);
        const minutes = time / 1000 / 60;
        return `${minutes} minutes`;
    }
};

Reward.prototype.getValue = function () {
    return this.value;
};

Reward.prototype.receiveReward = function () {
    if (this.getAmount()) {
        this.viewUpdate = this.descriptor.receive.call(this, this.value, this.options);
    }
};

Reward.prototype.updateViewAmount = function () {
    if (this.viewUpdate) {
        this.viewUpdate();
        delete this.viewUpdate;
    }
};

Reward.prototype.onAnimationFinished = function () {
    this.updateViewAmount();

    if (this.options.onFinish) {
        this.options.onFinish();
    }

    const amount = this.getAmount();
    if (!amount) {
        return;
    }

    const target = cleverapps.aims.getTarget(this.getFlyingTarget());

    if (!target || !target.aim || target.aim.noTargetDelta || target.aim.controls && target.aim.controls.indexOf("MenuBar") === 0) {
        return;
    }

    if (!target.aim.withoutDelta && !this.options.withoutDelta && amount) {
        const deltaIcon = this.getSmallIcon();
        deltaIcon.setScale(deltaIcon.scale * cleverapps.styles.Reward.jumpCollect.delta.scale);
        cleverapps.aims.showDelta(amount, target, {
            type: this.type,
            icon: deltaIcon,
            x: target.width * 0.5,
            y: target.height * 1.2
        });
    }

    cleverapps.Scenes.GetRunningScene().runAction(new cc.CollectEffect(target));
};

Reward.prototype.getAmount = function () {
    return this.descriptor.getAmount(this.value);
};

Reward.prototype.getIcon = function (regular) {
    let icon;

    if (this.options.icons && this.options.icons[this.type] && !regular) {
        icon = new cc.Sprite(this.options.icons[this.type]);
    } else if (this.descriptor.getIcon) {
        const frame = this.descriptor.getIcon(this.value, this.options);
        if (frame instanceof cc.Node) {
            icon = frame;
        } else if (frame) {
            icon = new cc.Sprite(frame);
        }
    } else if (this.descriptor.getAnimation) {
        const params = this.descriptor.getAnimation(this.value);
        if (params && params.json) {
            icon = new cleverapps.Spine(params.json);
            icon.setScale(params.scale || 1.0);
            icon.setAnimation(0, params.animation, true);

            if (params.skin) {
                icon.setSkin(params.skin);
            }
        }
    }

    if (!icon) {
        let message = `Reward icon is undefined - ${this.type}`;
        if (this.value && typeof this.value === "object") {
            message += ` ${JSON.stringify(this.value)}`;
        }
        if (cleverapps.config.debugMode) {
            throw message;
        }
        cleverapps.throwAsync(message);
        icon = new cc.Node();
    }

    return icon;
};

Reward.prototype.getSmallIcon = function (regular) {
    const styles = cleverapps.styles.Reward;
    const params = {};

    if (this.options.icons && this.options.icons[this.type] && !regular) {
        params.frame = this.options.icons[this.type];
    } else if (this.descriptor.getSmallIcon) {
        params.frame = this.descriptor.getSmallIcon(this.value);
    }

    let icon;

    if (params.frame) {
        icon = new cc.Sprite(params.frame);
    } else {
        icon = this.getIcon(regular);
    }

    cleverapps.UI.fitToBox(icon, styles.smallIcon);

    return icon;
};

Reward.prototype.getFlyingAmount = function () {
    return this.descriptor.getFlyingAmount(this.getAmount(), this.value);
};

Reward.prototype.getFlyingTarget = function (source) {
    if (this.options.target && cleverapps.aims.getTarget(this.options.target, { noDefault: true })) {
        return this.options.target;
    }

    return this.descriptor.getTarget(this.value, this.options, source);
};

Reward.prototype.getText = function (prefix) {
    return this.descriptor.getText(this.value, prefix);
};

Reward.prototype.listControls = function () {
    return cleverapps.toArray(this.descriptor.controls) || [];
};

Reward.prototype.listBundles = function () {
    const bundleData = this.descriptor.bundle;
    const bundles = typeof bundleData === "function" ? bundleData.call(this) : bundleData;
    return cleverapps.toArray(bundles) || [];
};

Reward.prototype.collectRewardsAction = function (source, options) {
    options = options || {};

    const amount = this.getAmount();
    if (!amount) {
        options.callback = options.callback || function () {};
        return new cc.CallFunc(options.callback);
    }

    const target = cleverapps.aims.getTarget(this.getFlyingTarget(source));

    if (!options.effect) {
        options.effect = this.descriptor.getFlyEffect(this.value);
    }

    if (!options.effect) {
        if (target.aim.types.indexOf("default") !== -1) {
            options.effect = cleverapps.skins.getSlot("flyToRewardBoxEffect");
        } else if (cleverapps.intersect(["hard", "soft", "exp", "worker", "wands"], target.aim.types).length === 0) {
            options.effect = cleverapps.skins.getSlot("expFlyFinishEffect");
        }
    }

    let flyingAnimation = Reward.COLLECT_ANIMATION;
    if (options.flyingAnimation) {
        flyingAnimation = options.flyingAnimation;
    } else if (this.descriptor.flyingAnimation) {
        flyingAnimation = this.descriptor.flyingAnimation;
    } else if (target.aim.flyingAnimation) {
        flyingAnimation = target.aim.flyingAnimation;
    }

    let flyAction, collectAction;

    if (source || Reward.isCustom(flyingAnimation)) {
        switch (flyingAnimation) {
            case Reward.SPAWN_UNITS_ANIMATION:
                flyAction = this.spawnUnitsAnimation(this.spawnedUnits, source, options);
                break;

            case Reward.SPAWN_CARDS_ANIMATION:
                flyAction = this.spawnCardsAnimation(this.spawnedCards, source, target, options);
                break;

            case Reward.JUMP_COLLECT_ANIMATION:
                flyAction = this.jumpCollectAnimation(source, target, options);
                break;

            case Reward.JUMP_COLLECT_ANIMATION2:
                flyAction = this.jumpCollectAnimation2(source, target, options);
                break;

            case Reward.COLLECT_ANIMATION:
                flyAction = this.collectAnimation(source, target, options);
                break;

            case Reward.CUSTOM_COLLECT:
                flyAction = this.customCollectAnimation(options);
                break;

            case Reward.NO_ANIMATION:
                if (source instanceof cc.Node && !options.keepSource) {
                    flyAction = new cc.CallFunc(() => {
                        source.setVisible(false);
                    });
                }
                break;
        }
    }

    collectAction = new cc.CallFunc(this.onAnimationFinished.bind(this));

    if (flyAction) {
        collectAction = new cc.Sequence(flyAction, collectAction);
    }

    let delay = options.delay || 0;

    if (target.aim.scrollsToTarget && target.aim.scrollsToTarget()) {
        const scrollAction = new cc.ScrollAction(target, {
            skipFocusReport: true,
            duration: 0.6,
            visibleBox: {
                left: 0.4,
                right: 0.4,
                top: 0.4,
                bottom: 0.4
            }
        });

        const locationComponent = typeof Map2dLocationSceneComponent !== "undefined" && cleverapps.Scenes.GetSceneComponent(Map2dLocationSceneComponent)
            || typeof Merge2SceneComponent !== "undefined" && cleverapps.Scenes.GetSceneComponent(Merge2SceneComponent)
            || typeof FarmSceneComponent !== "undefined" && cleverapps.Scenes.GetSceneComponent(FarmSceneComponent);

        collectAction = new cc.Sequence(
            new cc.CallFunc(() => {
                locationComponent.map.runAction(scrollAction);
            }),
            new cc.DelayTime(scrollAction.getDuration()),
            collectAction
        );

        delay += 0.6;
    }

    return new cc.Sequence(
        new cc.CallFunc(() => {
            if (options.deltaSource) {
                cleverapps.aims.showDelta(this.value, options.deltaSource, {
                    icon: this.getSmallIcon(),
                    type: this.type,
                    font: cleverapps.styles.FONTS.SCENE_ANIMATE_DELTA_TEXT
                });
            }
        }),
        new cc.DelayTime(delay / 1000),
        new cc.CallFunc(() => {
            const effect = this.descriptor.getStartReceiveRewardEffect(this.value);
            if (effect) {
                cleverapps.effects.fire(effect);
            }
        }),
        collectAction,
        new cc.CallFunc(options.callback)
    );
};

Reward.prototype.collectRewardsAnimation = function (source, options) {
    const scene = cleverapps.Scenes.GetRunningScene();
    const action = this.collectRewardsAction(source, options);
    scene.runAction(action).setFinalize(() => this.updateViewAmount());
};

Reward.prototype.collectAnimation = function (source, target, options) {
    source = source.onGetView ? source.onGetView() : source;
    if (!source) {
        return;
    }

    source.stopAllActions();

    const collectAction = StandartAnimations.animateCollect(source, target, {
        path: options.path,
        adjustToTarget: true,
        effect: options.effect,
        spawnHaptic: options.spawnHaptic,
        collectHaptic: options.collectHaptic,
        removeOnFinish: true
    });

    const actions = [];

    actions.push(new cc.CallFunc(() => {
        if (source.isRunning()) {
            source.runAction(collectAction);
        }
    }));

    actions.push(new cc.DelayTime(collectAction.getDuration()));

    actions.push(new cc.CallFunc(() => {
        cleverapps.aims.animateTarget(target, {
            delta: 1,
            withoutDelta: options.withoutDelta || options.deltaSource !== undefined
        });
    }));

    return new cc.Sequence(actions);
};

Reward.prototype.spawnUnitsAnimation = function (spawnedUnits, source) {
    source.replaceParentSamePlace(cleverapps.Scenes.GetMovingNode(source), {
        keepScale: true
    });

    const actions = [];

    actions.push(new cc.CallFunc(() => {
        if (cleverapps.config.type === "merge2") {
            Merge2.current.spawnAction(spawnedUnits, [], source, {
                fromNode: true,
                startScale: cc.Base.AdjustScaleTo(source, Merge2.current.field.getMapView().animations),
                startAnchors: {
                    anchorX: 0.5,
                    anchorY: 0.5
                }
            });
        } else {
            Map2dLocation.currentLocation.spawnAction(spawnedUnits, [], source, {
                fromNode: true,
                startScale: cc.Base.AdjustScaleTo(source, Map2d.currentMap.getMapView().animations),
                startAnchors: {
                    anchorX: 0.5,
                    anchorY: 0.5
                }
            });
        }
    }));

    actions.push(
        new cc.DelayTime(0.2),
        new cc.CallFunc(() => {
            source.setVisible(false);
        }),
        new cc.DelayTime(0.5),
        new cc.CallFunc(() => {
            source.removeFromParent();
        })
    );

    return new cc.Sequence(actions);
};

Reward.prototype.spawnCardsAnimation = function (spawnedCards, source, target) {
    const scene = cleverapps.Scenes.GetRunningScene();
    const position = scene.convertToNodeSpace(source.parent.convertToWorldSpace(source));

    cleverapps.aims.showTarget(target);

    const actions = [];

    spawnedCards.forEach((card) => {
        actions.push(new cc.DelayTime(0.3));
        actions.push(new cc.CallFunc(() => {
            cleverapps.aims.showTarget(target);

            const ViewClass = card.getViewClass();
            const view = new ViewClass(card);
            view.setAlignment(position);
            view.setScale(0.3);
            scene.addChild(view);

            Game.currentGame.hand.push(card, {
                insertIndex: PseudoRandom.GET().random(Game.currentGame.hand.stack.length),
                fromTable: true
            });

            cleverapps.effects.fire("solitaire.card.plus");
        }));
    });

    return new cc.Sequence(actions);
};

Reward.prototype.jumpCollectAnimation = function (source, target, options) {
    options = options || {};
    const styles = cleverapps.styles.Reward.jumpCollect;

    let movingNode;
    let startPosition;
    let fromMap2d;
    if (source instanceof cc.Node) {
        movingNode = cleverapps.Scenes.GetMovingNode(source);
        startPosition = movingNode.convertToNodeSpace(source.parent.convertToWorldSpace(source.getPosition()));
    } else {
        fromMap2d = true;
        movingNode = Map2d.currentMap.getMapView().animations;
        startPosition = Map2d.currentMap.getMapView().alignInGrid(source.x, source.y);
        const multiCell = source.findComponent && source.findComponent(MultiCell);
        if (multiCell) {
            const centerDelta = multiCell.getCenter();
            startPosition.x += centerDelta.x;
            startPosition.y += centerDelta.y;
        }
    }

    const beginScale = options.beginScale || 0.5;
    const flyingScale = options.flyingScale || styles.delta.scale;
    const finalScale = options.finalScale || flyingScale;
    const flyingAmount = this.getFlyingAmount();

    const actions = [];
    cleverapps.arrayFill(flyingAmount).forEach((value, index) => {
        const item = options.small === false ? this.getIcon(true) : this.getSmallIcon(true);
        const itemBeginScale = item.scale * beginScale;
        const itemFlyingScale = item.scale * flyingScale;
        const itemFinalScale = Math.min(target.getGlobalBoundingBox().width / item.width, finalScale);

        item.setScale(itemBeginScale);
        item.setAlignment(startPosition);
        movingNode.addChild(item);

        item.setLocalZOrder(1 / (1 + index));
        item.setVisible(false);

        const action = [
            new cc.DelayTime(index === 0 ? 0 : (index * 0.1 + Math.random() * 0.1) * (options.timeScale || 1)),
            new cc.Show(),
            new cc.CallFunc(() => {
                if (options.keepSource === false) {
                    source.setVisible(false);
                }
            }),
            StandartAnimations.jumpCollect(item, target, {
                collectMovingNode: fromMap2d && cleverapps.focusManager.currentWindow() && movingNode,
                jumpScale: itemFlyingScale,
                jumpOffset: index / flyingAmount,
                jumpSide: options.jumpSide,
                collectScale: itemFinalScale,
                collectSoundEffect: options.effect,
                spawnHaptic: options.spawnHaptic,
                landHaptic: options.landHaptic,
                collectHaptic: options.collectHaptic,
                jumpCollectEffect: cleverapps.skins.getSlot("jumpCollectEffect"),
                longJump: fromMap2d && cleverapps.skins.getSlot("useLongJumpAnimation"),
                timeScale: options.timeScale
            })
        ];

        if (index === 0) {
            action.push(new cc.CallFunc(() => {
                cleverapps.aims.showTarget(target);
                cleverapps.aims.animateTarget(target, {
                    delta: flyingAmount,
                    amount: this.getAmount(),
                    withoutDelta: options.withoutDelta || options.deltaSource !== undefined,
                    showSparks: options.showSparks
                });
            }));
        }
        action.push(new cc.RemoveSelf());

        const itemSequence = new cc.Sequence(action);

        actions.push(new cc.Sequence(
            new cc.CallFunc(() => {
                item.runAction(itemSequence);
            }),
            new cc.DelayTime(itemSequence.getDuration())
        ));
    });

    return new cc.Spawn(actions);
};

Reward.prototype.jumpCollectAnimation2 = function (source, target, options) {
    options = options || {};

    const movingNode = cleverapps.Scenes.GetMovingNode(source);
    const sourcePosition = movingNode.convertToNodeSpace(source.parent.convertToWorldSpace(source.getPosition()));

    const flyingAmount = this.getFlyingAmount();
    const sourceWidth = source.getGlobalBoundingBox().width;

    target = cleverapps.aims.getTarget(target);

    const actions = [
        new cc.CallFunc(() => {
            const sparks = new cleverapps.Spine(bundles.animations_library.jsons.sparks_json);
            movingNode.addChild(sparks, -1);
            sparks.setAlignment(sourcePosition);
            sparks.setSkin("yellow");
            sparks.setAnimation(0, "single", false);
            sparks.setCompleteListenerRemove();

            if (options.keepSource === false) {
                source.setVisible(false);
            }
        })
    ];
    cleverapps.arrayFill(flyingAmount).forEach((value, index) => {
        const item = options.small === false ? this.getIcon(true) : this.getSmallIcon(true);
        const beginScale = sourceWidth / item.width;
        const collectScale = beginScale * 1.1;
        const targetScale = Math.min(Math.min(target.getGlobalBoundingBox().width / item.width, 1));

        const maxRadius = 120;
        const minRadius = 20;
        
        const angle = -Math.PI + Math.random() * Math.PI;
        const radius = minRadius + Math.random() * (maxRadius - minRadius);

        const startDx = Math.cos(angle) * minRadius;
        const startDy = Math.sin(angle) * minRadius;

        const dx = Math.cos(angle) * radius;
        const dy = Math.sin(angle) * radius;

        const collectPosition = cc.p(sourcePosition.x + dx, sourcePosition.y + dy);

        item.setAlignment(sourcePosition.x + startDx, sourcePosition.y + startDy);
        movingNode.addChild(item);
        item.setScale(beginScale);

        const action = [
            new cc.Spawn(
                new cc.MoveTo(0.25, collectPosition),
                new cc.ScaleTo(0.25, collectScale)
            ),
            new cc.DelayTime(0.3 + index * 0.1),
            new cc.ReplaceParent(movingNode),
            new cc.CallFunc(() => {
                cleverapps.aims.showTarget(target);
            }),
            new cc.Spawn(
                new cc.CollectAnimation(0.5, target, {
                    straight: true
                }).easing(cc.easeIn(2)),
                new cc.ScaleTo(0.5, targetScale, targetScale)
            )
        ];

        if (options.effect) {
            actions.push(cleverapps.effects.fireAction(options.effect));
        }

        if (index === 0) {
            action.push(new cc.CallFunc(() => {
                cleverapps.aims.showTarget(target);
                cleverapps.aims.animateTarget(target, {
                    delta: flyingAmount,
                    amount: this.getAmount(),
                    withoutDelta: options.withoutDelta || options.deltaSource !== undefined
                });
            }));
        }
        action.push(new cc.RemoveSelf());

        const itemSequence = new cc.Sequence(action);

        actions.push(new cc.Sequence(
            new cc.CallFunc(() => {
                item.runAction(itemSequence);
            }),
            new cc.DelayTime(itemSequence.getDuration())
        ));
    });

    return new cc.Spawn(actions);
};

Reward.prototype.customCollectAnimation = function (options) {
    options = options || {};
    const movingNode = cleverapps.Scenes.GetMovingNode();

    const view = new CustomRewardView(this, options.customRewardIndex || 0);
    movingNode.addChild(view);

    return new cc.Sequence(
        new cc.CallFunc(() => {
            view.collectAnimate();
        }),
        new cc.DelayTime(view.getAnimationDuration())
    );
};

Reward.prototype.isFlyingUnderShadow = function () {
    const target = cleverapps.aims.getTarget(this.getFlyingTarget());
    return target && target.aim && target.aim.flyingUnderShadow;
};

Reward.prototype.isEqual = function (reward) {
    const type = Object.keys(reward)[0];
    if (type !== this.type) {
        return false;
    }
    if (typeof reward[type] === "number" || typeof reward[type] === "string") {
        return true;
    }
    return this.descriptor.matches(this.value, Object.keys(reward[type])[0]);
};

Reward.prototype.calcPrice = function () {
    return this.descriptor.getPrice(this.getValue());
};

Reward.NO_ANIMATION = 1;
Reward.COLLECT_ANIMATION = 2;
Reward.SPAWN_UNITS_ANIMATION = 3;
Reward.SPAWN_CARDS_ANIMATION = 4;
Reward.JUMP_COLLECT_ANIMATION = 5;
Reward.JUMP_COLLECT_ANIMATION2 = 6;
Reward.CUSTOM_COLLECT = 7;

Reward.TIME_VALUE_PRICE_PER_HOUR = 8;
Reward.LIVES_PRICE = 20;

Reward.isCustom = function (rewardAnimation) {
    return rewardAnimation === Reward.NO_ANIMATION || rewardAnimation === Reward.CUSTOM_COLLECT;
};

cleverapps.styles.Reward = {
    icon: {
        height: 130,
        maxScale: 1.5
    },

    smallIcon: {
        height: 70
    },

    jumpCollect: {
        delta: {
            dy: 80,
            scale: 0.8
        },

        jumpRight: {
            x1: 50,
            x2: 150,
            y1: -50,
            y2: -100
        },

        jumpLeft: {
            x1: -50,
            x2: -150,
            y1: -50,
            y2: -100
        },

        jumpBoth: {
            x1: -50,
            x2: 50,
            y1: -50,
            y2: -100
        }
    },

    flyHint: {
        speed: 3000
    }
};
